import { RegistrationMetrics } from "@/registration-tools/common/registration-report/registration-metrics";
import { THRESHOLD_SET_STATIONARY } from "@/registration-tools/common/registration-report/registration-thresholds";
import { useAppSelector } from "@/store/store-hooks";
import { FaroDialog, TranslateVar } from "@faro-lotv/flat-ui";
import { RegistrationEdgeRevision } from "@faro-lotv/service-wires";
import type { JSX } from "react";
import { TOOLTIP_Z_INDEX } from "../../rendering/registration-connections";
import { selectRevisionEntityScan } from "../../store/revision/revision-scan-selectors";

type ConnectionQualityDialogProps = {
  /** The registration edge entity to show the connection */
  registrationEdge: RegistrationEdgeRevision;

  /** The flag to check whether the dialog is open */
  isDialogOpen: boolean;

  /** Callback, when the dialog needs to be open/closed */
  onDialogClose(shouldOpen: boolean): void;
};

/**
 * @returns the quality dialog of the specific connection
 */
export function ConnectionQualityDialog({
  registrationEdge,
  isDialogOpen,
  onDialogClose,
}: ConnectionQualityDialogProps): JSX.Element | undefined {
  const { sourceId, targetId, data } = registrationEdge;
  const sourceEntity = useAppSelector(selectRevisionEntityScan(sourceId));
  const targetEntity = useAppSelector(selectRevisionEntityScan(targetId));

  if (!sourceEntity || !targetEntity) {
    return;
  }

  return (
    <FaroDialog
      title={
        <>
          Quality View:{" "}
          <TranslateVar name="source-scan">{sourceEntity.name}</TranslateVar> -{" "}
          <TranslateVar name="target-scan">{targetEntity.name}</TranslateVar>
        </>
      }
      aria-label="Detailed connection quality report"
      open={isDialogOpen}
      onClose={() => onDialogClose(false)}
      size="l"
      sx={{ zIndex: TOOLTIP_Z_INDEX }}
    >
      {data?.metrics?.overlap && data.metrics.rlyHistogram && (
        <RegistrationMetrics
          overlap={data.metrics.overlap}
          rlyHistogram={data.metrics.rlyHistogram}
          thresholdSet={THRESHOLD_SET_STATIONARY}
        />
      )}
    </FaroDialog>
  );
}
